import type { RefObject } from "react";
import { useEffect } from "react";

import type { CdropdownMode, CdropdownOption } from "./Cdropdown.types";
import { CDROPDOWN_MODE } from "./constants";
import { handleDropdownKeyboardEvent } from "./dropdown-keyboard";

interface UseDropdownRevealAnimationInput {
  isOpen: boolean;
  isSurfaceMeasured: boolean;
  setIsAnimating: (isAnimating: boolean) => void;
}

interface UseDropdownOutsideDismissInput {
  isOpen: boolean;
  dismissOnOutsideClick: boolean;
  mode: CdropdownMode;
  dropdownRef: RefObject<HTMLDivElement | null>;
  triggerRef: RefObject<HTMLDivElement | null>;
  handleClose: () => void;
}

interface UseDropdownKeyboardNavigationInput {
  isOpen: boolean;
  keyboardNavigation: boolean;
  options: CdropdownOption[];
  effectiveActiveIndex: number | null;
  handleClose: () => void;
  setActiveIndex: (index: number | null) => void;
  onOptionClick: (option: CdropdownOption) => void;
}

interface UseDropdownInteractionEffectsInput
  extends UseDropdownRevealAnimationInput,
    UseDropdownOutsideDismissInput,
    UseDropdownKeyboardNavigationInput {}

function shouldDismissDropdownFromTarget(input: {
  target: EventTarget | null;
  mode: CdropdownMode;
  dropdownElement: HTMLDivElement | null;
  triggerElement: HTMLDivElement | null;
}): boolean {
  const { target } = input;
  if (!isNodeEventTarget(target)) {
    return false;
  }

  if (
    isDropdownElementTarget({
      target,
      dropdownElement: input.dropdownElement,
    })
  ) {
    return false;
  }

  if (
    isTriggerElementTarget({
      target,
      mode: input.mode,
      triggerElement: input.triggerElement,
    })
  ) {
    return false;
  }

  return true;
}

function isNodeEventTarget(target: EventTarget | null): target is Node {
  return target instanceof Node;
}

function isDropdownElementTarget(input: {
  target: Node;
  dropdownElement: HTMLDivElement | null;
}): boolean {
  return Boolean(input.dropdownElement?.contains(input.target));
}

function isTriggerElementTarget(input: {
  target: Node;
  mode: CdropdownMode;
  triggerElement: HTMLDivElement | null;
}): boolean {
  if (input.mode !== CDROPDOWN_MODE.Trigger) {
    return false;
  }

  return Boolean(input.triggerElement?.contains(input.target));
}

function useDropdownRevealAnimation({
  isOpen,
  isSurfaceMeasured,
  setIsAnimating,
}: UseDropdownRevealAnimationInput): void {
  useEffect(() => {
    if (!isOpen || !isSurfaceMeasured) {
      return;
    }

    const revealRaf = window.requestAnimationFrame(() => {
      setIsAnimating(true);
    });

    return () => {
      window.cancelAnimationFrame(revealRaf);
    };
  }, [isOpen, isSurfaceMeasured, setIsAnimating]);
}

function useDropdownOutsideDismiss({
  isOpen,
  dismissOnOutsideClick,
  mode,
  dropdownRef,
  triggerRef,
  handleClose,
}: UseDropdownOutsideDismissInput): void {
  useEffect(() => {
    if (!isOpen || !dismissOnOutsideClick) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        !shouldDismissDropdownFromTarget({
          target: event.target,
          mode,
          dropdownElement: dropdownRef.current,
          triggerElement: triggerRef.current,
        })
      ) {
        return;
      }

      handleClose();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [
    dismissOnOutsideClick,
    dropdownRef,
    handleClose,
    isOpen,
    mode,
    triggerRef,
  ]);
}

function useDropdownKeyboardNavigation({
  isOpen,
  keyboardNavigation,
  options,
  effectiveActiveIndex,
  handleClose,
  setActiveIndex,
  onOptionClick,
}: UseDropdownKeyboardNavigationInput): void {
  useEffect(() => {
    if (!isOpen || !keyboardNavigation) {
      return;
    }

    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      handleDropdownKeyboardEvent({
        event,
        options,
        effectiveActiveIndex,
        closeDropdown: handleClose,
        onActiveIndexChange: setActiveIndex,
        onOptionSelect: onOptionClick,
      });
    };

    document.addEventListener("keydown", handleDocumentKeyDown);
    return () => {
      document.removeEventListener("keydown", handleDocumentKeyDown);
    };
  }, [
    effectiveActiveIndex,
    handleClose,
    isOpen,
    keyboardNavigation,
    onOptionClick,
    options,
    setActiveIndex,
  ]);
}

export function useDropdownInteractionEffects({
  isOpen,
  isSurfaceMeasured,
  setIsAnimating,
  dismissOnOutsideClick,
  mode,
  dropdownRef,
  triggerRef,
  handleClose,
  keyboardNavigation,
  options,
  effectiveActiveIndex,
  setActiveIndex,
  onOptionClick,
}: UseDropdownInteractionEffectsInput): void {
  useDropdownRevealAnimation({ isOpen, isSurfaceMeasured, setIsAnimating });
  useDropdownOutsideDismiss({
    isOpen,
    dismissOnOutsideClick,
    mode,
    dropdownRef,
    triggerRef,
    handleClose,
  });
  useDropdownKeyboardNavigation({
    isOpen,
    keyboardNavigation,
    options,
    effectiveActiveIndex,
    handleClose,
    setActiveIndex,
    onOptionClick,
  });
}
